import HeaderWithSearch from "@components/SearchHeader/HeaderWithSearch";
import React, { useCallback, useEffect, useState } from "react";
import {
  BackHandler,
  FlatList,
  View,
  StyleSheet,
  ActivityIndicator,
  Alert,
} from "react-native";
import SearchIcon from "@assets/svg/SearchIcon";
import { getOutForDeliveryDbDetails, markOutForDelivery } from "@services/api";
import { useFocusEffect } from "@react-navigation/native";
import _, { debounce } from "lodash";
import color from "@constants/color";
import { format } from "date-fns";
import TagList from "./components/TagList";
import SummaryData from "./components/SummaryData";
import UserCard from "./components/UserCard";
import BottomButton from "./components/BottomButton";
import Filter from "./components/Filter";
import ConfirmationModal from "./components/ConfirmationModal";

const OutForDeliveryDetails = ({ navigation, route }) => {
  const [ofdData, setOfdData] = useState({});
  const [selectedRouteId, setSelectedRouteId] = useState("ALL");
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [notMarkedCount, setNotMarkedCount] = useState(0);
  const [draftOfd, setDraftOfd] = useState({});
  const { db } = route.params;

  useFocusEffect(
    useCallback(() => {
      fetchData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRouteId]),
  );
  useEffect(() => {
    const backHandler = () => {
      if (isSearchEnabled) {
        onSearchInputChange("");
        setIsSearchEnabled((prev) => !prev);
        return true;
      }
      return false;
    };
    const subscription = BackHandler.addEventListener("hardwareBackPress", backHandler);
    return () => {
      subscription.remove();
    };
  }, [isSearchEnabled, onSearchInputChange]);

  const fetchData = async ({ searchTerm = "", isSearch = true } = {}) => {
    try {
      setIsLoading(true);
      const { data } = await getOutForDeliveryDbDetails({
        db_id: db?.db_id,
        route_id: selectedRouteId,
        search_term: isSearch ? searchTerm || searchText : "",
      });
      setOfdData(data?.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onRefresh = () => {
    setIsRefreshing(true);
    fetchData();
    setIsRefreshing(false);
  };

  const onPressMarkOfdBtn = async () => {
    const allNotMarkOfdCount = ofdData?.customersList?.filter(
      (item) => item?.is_ofd === false,
    ).length;
    const allDraftOfdCount = Object.keys(draftOfd).length;
    if (allNotMarkOfdCount !== allDraftOfdCount) {
      setNotMarkedCount(allNotMarkOfdCount - allDraftOfdCount);
      setShowConfirmationModal(true);
    } else {
      onPressMarkOfd();
    }
  };

  const onPressMarkOfd = async () => {
    const today = format(new Date(), "yyyy-MM-dd");
    if (ofdData?.ofdDate !== today) {
      Alert.alert(
        "Failed",
        "You can only mark out for delivery for today's date.",
      );
      return;
    }
    try {
      setShowConfirmationModal(false);
      setIsLoading(true);
      const data = Object.values(draftOfd);
      const payload = {
        ofd_date: ofdData?.ofdDate,
        customers_list: data,
      };
      await markOutForDelivery({ payload, db_id: db?.db_id });
      setDraftOfd({});
      fetchData();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const changeRoute = (routeId) => {
    setSelectedRouteId(routeId);
    setDraftOfd({});
  };
  const goingBackHandler = () => {
    onSearchInputChange("");
    setIsSearchEnabled(false);
  };

  const onSearchInputChange = debounce((text) => {
    setSearchText(text);
    fetchData({ searchTerm: text, isSearch: text.length > 0 });
  }, 500);
  const onToggleUserSwitch = (userData) => {
    const {
      user_id,
      user_name,
      user_address,
      user_phone,
      user_lat,
      user_lng,
      route_id,
      route_name,
      order_ids,
      is_ofd,
      status,
      bag_names,
      bag_ids,
      show_no_bag_icon,
      address_id = "",
    } = userData;
    const draftOfdCopy = _.clone(draftOfd);
    if (
      draftOfdCopy[`${user_id}_${address_id}`] &&
      draftOfdCopy[`${user_id}_${address_id}`].user_id === user_id
    ) {
      delete draftOfdCopy[`${user_id}_${address_id}`];
    } else {
      _.set(draftOfdCopy, `${user_id}_${address_id}`, {
        user_id,
        user_name,
        user_address,
        user_phone,
        user_lat,
        user_lng,
        route_id,
        route_name,
        order_ids,
        is_ofd,
        status,
        bag_names,
        bag_ids,
        show_no_bag_icon,
        address_id,
      });
    }
    setDraftOfd(draftOfdCopy);
  };

  const onToggleAllSwitch = (selectAll) => {
    const draftOfdCopy = _.cloneDeep(draftOfd);
    if (selectAll) {
      ofdData?.customersList?.forEach((item) => {
        if (
          item?.is_ofd === false &&
          item?.status === "PACKED" &&
          item?.enable_ofd === true
        ) {
          const {
            user_id,
            user_name,
            user_address,
            user_phone,
            user_lat,
            user_lng,
            route_id,
            route_name,
            order_ids,
            is_ofd,
            status,
            bag_names,
            bag_ids,
            show_no_bag_icon,
            address_id = "",
          } = item;
          _.set(draftOfdCopy, `${user_id}_${address_id}`, {
            user_id,
            user_name,
            user_address,
            user_phone,
            user_lat,
            user_lng,
            route_id,
            route_name,
            order_ids,
            is_ofd,
            status,
            bag_names,
            bag_ids,
            show_no_bag_icon,
            address_id,
          });
        }
      });
    } else {
      Object.keys(draftOfdCopy).forEach((key) => {
        delete draftOfdCopy[key];
      });
    }
    setDraftOfd(draftOfdCopy);
  };

  const isToggleOn = ({ userData }) => {
    return (
      draftOfd[`${userData?.user_id}_${userData?.address_id || ""}`] &&
      draftOfd[`${userData?.user_id}_${userData?.address_id || ""}`].user_id ===
        userData.user_id
    );
  };

  const _headerData = [
    {
      key: "ROUTE_LIST",
    },
    {
      key: "SUMMARY",
    },
    {
      key: "FILTER",
    },
  ];

  const _searchHeaderData = [
    {
      key: "SPACER",
    },
  ];

  const renderItem = ({ item }) => {
    if (item.key === "ROUTE_LIST") {
      return (
        <TagList
          tags={ofdData?.tags || []}
          selectedRouteId={selectedRouteId}
          onPress={changeRoute}
        />
      );
    } else if (item.key === "SUMMARY") {
      return (
        <SummaryData
          summary={ofdData?.summary || {}}
          disabled_reason_submit={ofdData?.disabled_reason_submit}
        />
      );
    } else if (item.key === "FILTER") {
      return <Filter item={item} onToggleAllSwitch={onToggleAllSwitch} />;
    } else if (item.key === "SPACER") {
      return <View style={{ height: 10 }} />;
    } else {
      return (
        <UserCard
          item={item}
          onToggleUserSwitch={onToggleUserSwitch}
          isToggleOn={isToggleOn({ userData: item })}
        />
      );
    }
  };

  const renderFooter = () => {
    return <View style={{ height: 80 }} />;
  };

  return (
    <View style={{ flex: 1 }}>
      <HeaderWithSearch
        headertitle={db?.db_name || "Delivery Boy"}
        headerColor="#fff"
        hideShadow={true}
        navigation={navigation}
        route={route}
        searchPlaceholder="Search customer, address, number"
        isSearchEnabled={isSearchEnabled}
        goingBackHandler={isSearchEnabled ? goingBackHandler : null}
        searchIcon={<SearchIcon height={24} width={24} />}
        onSearchInputChange={onSearchInputChange}
        onPressCrossIcon={() => onSearchInputChange("")}
        onPressSearchIcon={() => setIsSearchEnabled(true)}
      />
      {isLoading ? (
        <ActivityIndicator
          size="large"
          color={color.primary}
          style={styles.loading}
        />
      ) : (
        <>
          <FlatList
            showsVerticalScrollIndicator={false}
            data={
              isSearchEnabled
                ? _searchHeaderData.concat(ofdData?.customersList || [])
                : _headerData.concat(ofdData?.customersList || [])
            }
            keyExtractor={(item) => item.user_id}
            renderItem={renderItem}
            stickyHeaderIndices={[0]}
            ListFooterComponent={renderFooter}
            refreshing={isRefreshing}
            onRefresh={onRefresh}
          />
          <BottomButton
            title={`Mark Out for delivery ${
              Object.keys(draftOfd).length > 0
                ? `(${Object.keys(draftOfd).length})`
                : ""
            }`}
            disable={
              !(Object.keys(draftOfd).length > 0) || !ofdData?.enable_ofd_submit
            }
            onPress={onPressMarkOfdBtn}
            loading={isLoading}
          />
        </>
      )}
      <ConfirmationModal
        showConfirmationModal={showConfirmationModal}
        setShowConfirmationModal={setShowConfirmationModal}
        customerCount={notMarkedCount}
        onPressConfirm={onPressMarkOfd}
      />
    </View>
  );
};
export default OutForDeliveryDetails;

const styles = StyleSheet.create({
  loading: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
  },
});
